/**
 * COS Awards Section Custom Element
 * Handles interactive features for award badge selection and content switching
 * Extends HTMLElement for better encapsulation and accessibility
 */

class CosAwardsSection extends HTMLElement {
  constructor() {
    super();
    this.currentActiveIndex = 0;
    this.awardBadges = [];
    this.contentItems = [];
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    // Cleanup when element is removed
    this.removeEventListeners();
  }

  init() {
    // Get all award badges and content items
    this.awardBadges = this.querySelectorAll(".award-badge");
    this.contentItems = this.querySelectorAll(".award-content-item");

    // Setup component functionality
    this.bindEvents();
    this.setupAccessibility();
    this.initializeActiveStates();
  }

  bindEvents() {
    // Award badge clicks
    this.addEventListener("click", (e) => {
      const badge = e.target.closest(".award-badge");
      if (badge) {
        e.preventDefault();
        this.handleAwardSelect(badge);
      }
    });

    // Keyboard events for accessibility
    this.addEventListener("keydown", (e) => {
      const badge = e.target.closest(".award-badge");
      if (badge) {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.handleAwardSelect(badge);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          this.navigateAwards("previous");
        } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          this.navigateAwards("next");
        } else if (e.key === "Home") {
          e.preventDefault();
          this.selectAward(0);
        } else if (e.key === "End") {
          e.preventDefault();
          this.selectAward(this.awardBadges.length - 1);
        }
      }
    });
  }

  removeEventListeners() {
    // Remove event listeners when component is destroyed
    this.removeEventListener("click", this.handleAwardSelect);
    this.removeEventListener("keydown", this.navigateAwards);
  }

  handleAwardSelect(badge) {
    const awardIndex = parseInt(badge.dataset.awardIndex);
    if (awardIndex !== this.currentActiveIndex) {
      this.selectAward(awardIndex);

      // Track award selection event
      this.trackEvent("award_selected", {
        award_index: awardIndex,
        award_label:
          badge.querySelector(".award-label")?.textContent?.trim() ||
          `Award ${awardIndex + 1}`,
      });
    }
  }

  selectAward(index) {
    if (index < 0 || index >= this.awardBadges.length) {
      return;
    }

    // Update active badge
    this.updateActiveBadge(index);

    // Update active content
    this.updateActiveContent(index);

    // Update current index
    this.currentActiveIndex = index;
  }

  updateActiveBadge(newIndex) {
    this.awardBadges.forEach((badge, index) => {
      const isActive = index === newIndex;

      // Visual state
      if (isActive) {
        badge.classList.add("active");
      } else {
        badge.classList.remove("active");
      }

      // ARIA state
      badge.setAttribute("aria-pressed", isActive.toString());

      // Tabindex for keyboard navigation
      badge.setAttribute("tabindex", isActive ? "0" : "-1");
    });
  }

  updateActiveContent(newIndex) {
    this.contentItems.forEach((content, index) => {
      const isActive = index === newIndex;

      if (isActive) {
        content.classList.remove("hidden");
        content.classList.add("active");

        // Smooth fade-in animation
        content.style.opacity = "0";
        content.style.transform = "translateY(10px)";

        requestAnimationFrame(() => {
          content.style.transition = "opacity 0.3s ease, transform 0.3s ease";
          content.style.opacity = "1";
          content.style.transform = "translateY(0)";
        });
      } else {
        content.classList.add("hidden");
        content.classList.remove("active");
        content.style.opacity = "";
        content.style.transform = "";
        content.style.transition = "";
      }
    });
  }

  navigateAwards(direction) {
    let newIndex;

    if (direction === "next") {
      newIndex = (this.currentActiveIndex + 1) % this.awardBadges.length;
    } else if (direction === "previous") {
      newIndex =
        (this.currentActiveIndex - 1 + this.awardBadges.length) %
        this.awardBadges.length;
    }

    this.selectAward(newIndex);

    // Focus the newly selected badge
    this.awardBadges[newIndex].focus();
  }

  initializeActiveStates() {
    // Ensure the first award is active by default
    if (this.awardBadges.length > 0) {
      this.selectAward(0);
    }
  }

  setupAccessibility() {
    // Set up ARIA attributes for award badges
    this.awardBadges.forEach((badge, index) => {
      const label =
        badge.querySelector(".award-label")?.textContent?.trim() ||
        `Award ${index + 1}`;

      // ARIA attributes
      badge.setAttribute("role", "button");
      badge.setAttribute("aria-pressed", index === 0 ? "true" : "false");
      badge.setAttribute("aria-label", `Select ${label}`);
      badge.setAttribute("tabindex", index === 0 ? "0" : "-1");

      // Add keyboard hint for screen readers
      if (index === 0) {
        badge.setAttribute("aria-describedby", "awards-keyboard-hint");
      }
    });

    // Add keyboard navigation hint (hidden visually but available to screen readers)
    const keyboardHint = document.createElement("div");
    keyboardHint.id = "awards-keyboard-hint";
    keyboardHint.className = "sr-only";
    keyboardHint.textContent =
      "Use arrow keys to navigate between awards, Enter or Space to select";
    this.appendChild(keyboardHint);

    // Set up ARIA live region for content changes
    const contentContainer = this.querySelector(".award-content");
    if (contentContainer) {
      contentContainer.setAttribute("aria-live", "polite");
      contentContainer.setAttribute("aria-atomic", "true");
    }

    // Add role for the entire awards section
    this.setAttribute("role", "tablist");
    this.setAttribute("aria-label", "Awards showcase");
  }

  trackEvent(eventName, data = {}) {
    // Analytics tracking - integrate with your analytics solution
    if (typeof gtag !== "undefined") {
      gtag("event", eventName, {
        event_category: "cos_awards_section",
        ...data,
      });
    }

    if (typeof fbq !== "undefined") {
      fbq("trackCustom", `CosAwards_${eventName}`, data);
    }

    // Console log for debugging
    console.log(`COS Awards Event: ${eventName}`, data);
  }

  // Public method to programmatically select an award
  selectAwardByIndex(index) {
    this.selectAward(index);
  }

  // Public method to get current active award
  getCurrentAward() {
    return {
      index: this.currentActiveIndex,
      badge: this.awardBadges[this.currentActiveIndex],
      content: this.contentItems[this.currentActiveIndex],
    };
  }
}

// Register the custom element
customElements.define("cos-awards-section", CosAwardsSection);

// Export for potential use in other modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = CosAwardsSection;
}
